import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Box,
  Slide,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { layout } from "../../theme/tokens";

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * A dialog on desktop; a full-screen, slide-up sheet on mobile — the pattern
 * native apps use for create/edit flows.
 */
export default function FormSheet({
  open,
  onClose,
  title,
  subtitle,
  children,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  onSubmit,
  submitting = false,
  submitDisabled = false,
  maxWidth = "sm",
  hideActions = false,
}) {
  const fullScreen = useMediaQuery((t) => t.breakpoints.down(layout.mobileBreakpoint));

  const submitButton = !hideActions && (
    <Button
      variant="contained"
      onClick={onSubmit}
      disabled={submitting || submitDisabled}
      type={onSubmit ? "button" : "submit"}
    >
      {submitting ? "Saving…" : submitLabel}
    </Button>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={maxWidth}
      TransitionComponent={fullScreen ? SlideUp : undefined}
    >
      {fullScreen ? (
        // Mobile: sticky sheet header with close + inline submit.
        <AppBar position="sticky" sx={{ pt: "env(safe-area-inset-top)" }}>
          <Toolbar sx={{ gap: 1, minHeight: `${layout.topBarHeight}px !important` }}>
            <IconButton edge="start" onClick={onClose} aria-label="Close">
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }} noWrap>
              {title}
            </Typography>
            {!hideActions && (
              <Button
                onClick={onSubmit}
                disabled={submitting || submitDisabled}
                sx={{ fontWeight: 600 }}
              >
                {submitting ? "Saving…" : submitLabel}
              </Button>
            )}
          </Toolbar>
        </AppBar>
      ) : (
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h4">{title}</Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ mt: -0.5, mr: -1 }} aria-label="Close">
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      )}

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2 },
          // Keep the last field clear of the home indicator on mobile.
          pb: fullScreen ? "calc(24px + env(safe-area-inset-bottom))" : 2,
        }}
      >
        {fullScreen && subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}
        {children}
      </DialogContent>

      {!fullScreen && !hideActions && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button onClick={onClose} color="inherit">
            {cancelLabel}
          </Button>
          {submitButton}
        </DialogActions>
      )}
    </Dialog>
  );
}
